<template>
  <div class="flex flex-col gap-4">
    <TextHeader>Dashboard</TextHeader>
    <template v-if="assetsShown.length > 0">
      <TextTitle
        class="hover:text-primary active:text-primary-600 w-fit cursor-pointer"
        @click="onAssetsTitleClicked"
      >
        Realtime vermogen
      </TextTitle>
      <div class="grid gap-4 min-[2048px]:grid-cols-3 xl:grid-cols-2">
        <AssetChartPanel
          v-for="asset in assetsShown"
          :key="asset.id"
          :metering-point="asset"
          :date-range="[startOfToday(), startOfToday()]"
          show-title
          show-asset-details-button
        />
      </div>
      <BaseButton
        v-if="hiddenAssetsCount > 0"
        plain
        class="mx-auto w-fit"
        @click="onShowMore"
      >
        <BaseIcon name="arrow-down-double" class="mr-2" />
        Show {{ Math.min(4, hiddenAssetsCount) }} more asset{{
          hiddenAssetsCount > 1 ? "s" : ""
        }}
      </BaseButton>
      <BaseDivider />
    </template>
    <template v-if="filterStore.currentPortfolio">
      <div>
        <TextTitle
          class="hover:text-primary active:text-primary-600 w-fit cursor-pointer"
          @click="onInsightsTitleClicked"
        >
          {{ filterStore.currentPortfolio.displayName }} inzichten
          {{ getYear(startOfToday()) }}
        </TextTitle>
        <TextDescription> De inzichten over het lopende jaar. </TextDescription>
      </div>
      <InsightsChartPanel
        :metering-point="filterStore.currentPortfolio"
        :date-range="[
          startOfYear(startOfToday()),
          lastDayOfYear(startOfToday()),
        ]"
      />
    </template>
    <div v-else>
      <TextTitle @click="onInsightsTitleClicked">
        Inzichten {{ getYear(startOfToday()) }}
      </TextTitle>
      <TextDescription class="italic">
        Kies een portfolio om inzichten over het lopende jaar te zien.
      </TextDescription>
    </div>
    <BaseDivider />
    <TextTitle
      class="hover:text-primary active:text-primary-600 w-fit cursor-pointer"
      @click="onMarketsTitleClicked"
      >Marktinzichten</TextTitle
    >
    <div class="grid grid-cols-1 gap-2 md:gap-4">
      <DayAheadChartPanel :date-range="[startOfToday(), startOfToday()]" />
      <ImbalanceChartPanel :date-range="[startOfToday(), startOfToday()]" />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue"
import { useRouter } from "vue-router"
import { startOfToday, getYear, startOfYear, lastDayOfYear } from "date-fns"
import { triggerEvent } from "@/services/plausibleService.js"
import { ASSET_TYPE_HIDDEN_MEASUREMENTS } from "@/stores/repower/meteringPointStoreFactories.js"
import useDashboardStore from "@/stores/dashboardStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  TextHeader,
  TextTitle,
  TextDescription,
  BaseDivider,
  BaseButton,
  BaseIcon,
} from "@repowerednl/ui-component-library"
import AssetChartPanel from "@/components/panels/AssetChartPanel.vue"
import InsightsChartPanel from "@/components/panels/insights/InsightsChartPanel.vue"
import DayAheadChartPanel from "@/components/panels/DayAheadChartPanel.vue"
import ImbalanceChartPanel from "@/components/panels/ImbalanceChartPanel.vue"
import usePortfolioAsDefaultMeteringPoint from "@/composables/usePortfolioAsDefaultMeteringPoint.js"

const router = useRouter()
const dashboardStore = useDashboardStore()
const filterStore = useFilterStore()

usePortfolioAsDefaultMeteringPoint()

const assetsNoConsumers = computed(() => {
  return filterStore.currentPortfolioAssets.filter(
    (asset) => !ASSET_TYPE_HIDDEN_MEASUREMENTS.includes(asset.type),
  )
})

const assetsShown = computed(() => {
  return assetsNoConsumers.value.slice(0, dashboardStore.limit)
})

const hiddenAssetsCount = computed(() => {
  return assetsNoConsumers.value.length - dashboardStore.limit
})

function onShowMore() {
  dashboardStore.limit = Math.min(
    dashboardStore.limit + 4,
    assetsNoConsumers.value.length,
  )
  triggerEvent("dashboard:show-more-click")
}

function onAssetsTitleClicked() {
  router.push({ name: "assets" })
  triggerEvent("dashboard:assets-title-click")
}

async function onInsightsTitleClicked() {
  await router.push({ name: "insights" })
  filterStore.setDateRange([
    startOfYear(startOfToday()),
    lastDayOfYear(startOfToday()),
  ])
  filterStore.setMode("both")
  triggerEvent("dashboard:insights-title-click")
}

function onMarketsTitleClicked() {
  router.push({ name: "markets" })
  triggerEvent("dashboard:markets-title-click")
}
</script>
